import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Building2, Plus, Loader2, Pencil, CalendarClock, Coins } from "lucide-react";
import { toast } from "sonner";
import CreditDialog, { CreditBadges, LowCreditClinics } from "@/components/CreditDialog";

export default function AdminPanel() {
    const [clinics, setClinics] = useState([]);
    const [stats, setStats] = useState({});
    const [open, setOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({
        clinic_name: "",
        name: "",
        email: "",
        password: "",
    });
    const [editOpen, setEditOpen] = useState(false);
    const [editForm, setEditForm] = useState({
        clinic_id: "",
        clinic_name: "",
        name: "",
        email: "",
        password: "",
    });
    const [editSaving, setEditSaving] = useState(false);
    const [licOpen, setLicOpen] = useState(false);
    const [licForm, setLicForm] = useState({
        clinic_id: "",
        clinic_name: "",
        days: 30,
    });
    const [licSaving, setLicSaving] = useState(false);
    const [creditClinicId, setCreditClinicId] = useState(null);
    const creditClinic = clinics.find((c) => c.id === creditClinicId);

    async function load() {
        const [c, s] = await Promise.all([
            api.get("/admin/clinics"),
            api.get("/admin/stats"),
        ]);
        setClinics(c.data);
        setStats(s.data);
    }

    useEffect(() => {
        load();
    }, []);

    async function submit(e) {
        e.preventDefault();
        setSaving(true);
        try {
            await api.post("/admin/patrons", form);
            toast.success("Patron hesabı oluşturuldu");
            setOpen(false);
            setForm({
                clinic_name: "",
                name: "",
                email: "",
                password: "",
            });
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    }

    async function toggle(id) {
        try {
            await api.patch(`/admin/clinics/${id}/toggle`);
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    async function openEdit(clinicId) {
        try {
            const { data } = await api.get(
                `/admin/clinics/${clinicId}/patron`,
            );
            setEditForm({
                clinic_id: data.clinic_id,
                clinic_name: data.clinic_name,
                name: data.name,
                email: data.email,
                password: "",
            });
            setEditOpen(true);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    async function submitEdit(e) {
        e.preventDefault();
        setEditSaving(true);
        try {
            const payload = {
                clinic_name: editForm.clinic_name,
                name: editForm.name,
                email: editForm.email,
            };
            if (editForm.password && editForm.password.trim()) {
                payload.password = editForm.password;
            }
            await api.patch(
                `/admin/clinics/${editForm.clinic_id}/patron`,
                payload,
            );
            toast.success("Patron bilgileri güncellendi");
            setEditOpen(false);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setEditSaving(false);
        }
    }

    function openLicense(clinic) {
        setLicForm({
            clinic_id: clinic.id,
            clinic_name: clinic.name,
            days: 30,
        });
        setLicOpen(true);
    }

    async function submitLicense(e) {
        e.preventDefault();
        setLicSaving(true);
        try {
            const { data } = await api.post(
                `/admin/clinics/${licForm.clinic_id}/extend`,
                { days: Number(licForm.days) },
            );
            toast.success(
                `Lisans ${licForm.days} gün uzatıldı — bitiş ${data.license_until.slice(0, 10)}`,
            );
            setLicOpen(false);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLicSaving(false);
        }
    }

    return (
        <div data-testid="admin-panel">
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h1 className="text-3xl font-bold text-slate-900">
                        Klinikler & Patronlar
                    </h1>
                    <p className="text-slate-600 mt-1">
                        Süper admin paneli — kliniklerinize patron hesabı açın
                    </p>
                </div>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button
                            className="dk-btn-primary rounded-full"
                            data-testid="create-patron-btn"
                        >
                            <Plus className="w-4 h-4 mr-1" /> Yeni Patron &
                            Klinik
                        </Button>
                    </DialogTrigger>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Yeni Klinik & Patron</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={submit} className="space-y-3">
                            <div>
                                <Label>Klinik Adı</Label>
                                <Input
                                    required
                                    value={form.clinic_name}
                                    onChange={(e) =>
                                        setForm({
                                            ...form,
                                            clinic_name: e.target.value,
                                        })
                                    }
                                    data-testid="patron-clinic-input"
                                />
                            </div>
                            <div>
                                <Label>Patron Adı</Label>
                                <Input
                                    required
                                    value={form.name}
                                    onChange={(e) =>
                                        setForm({
                                            ...form,
                                            name: e.target.value,
                                        })
                                    }
                                    data-testid="patron-name-input"
                                />
                            </div>
                            <div>
                                <Label>Kullanıcı Adı</Label>
                                <Input
                                    required
                                    type="text"
                                    value={form.email}
                                    onChange={(e) =>
                                        setForm({
                                            ...form,
                                            email: e.target.value,
                                        })
                                    }
                                    data-testid="patron-email-input"
                                />
                            </div>
                            <div>
                                <Label>Şifre</Label>
                                <Input
                                    required
                                    type="text"
                                    value={form.password}
                                    onChange={(e) =>
                                        setForm({
                                            ...form,
                                            password: e.target.value,
                                        })
                                    }
                                    data-testid="patron-password-input"
                                />
                            </div>
                            <Button
                                type="submit"
                                className="dk-btn-primary w-full"
                                disabled={saving}
                                data-testid="patron-submit-btn"
                            >
                                {saving ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    "Oluştur"
                                )}
                            </Button>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            <div className="grid md:grid-cols-4 gap-4 mb-8">
                {[
                    { l: "Klinik", v: stats.clinics ?? 0 },
                    { l: "Patron", v: stats.patrons ?? 0 },
                    { l: "Personel", v: stats.staff ?? 0 },
                    { l: "Hasta", v: stats.patients ?? 0 },
                ].map((s, i) => (
                    <div key={i} className="dk-stat-card">
                        <div className="text-xs uppercase tracking-wider text-slate-500 font-medium">
                            {s.l}
                        </div>
                        <div className="text-3xl font-bold text-slate-900 mt-2">
                            {s.v}
                        </div>
                    </div>
                ))}
            </div>

            <LowCreditClinics clinics={clinics} onOpen={setCreditClinicId} />

            <Card>
                <CardContent className="p-0">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-600">
                                <th className="px-6 py-3">Klinik</th>
                                <th className="px-6 py-3">Patron Kullanıcı Adı</th>
                                <th className="px-6 py-3">Lisans</th>
                                <th className="px-6 py-3">Kontür</th>
                                <th className="px-6 py-3">Durum</th>
                                <th className="px-6 py-3 text-right">
                                    İşlem
                                </th>
                            </tr>
                        </thead>
                        <tbody data-testid="clinics-table-body">
                            {clinics.map((c) => {
                                const dr = c.days_remaining;
                                let licLabel = "Lisans yok";
                                let licClass =
                                    "bg-slate-200 text-slate-700";
                                if (c.license_until) {
                                    if (c.is_expired) {
                                        licLabel = `Süresi doldu (${c.license_until.slice(0, 10)})`;
                                        licClass = "bg-rose-100 text-rose-800";
                                    } else if (dr !== null && dr <= 7) {
                                        licLabel = `${dr} gün kaldı`;
                                        licClass =
                                            "bg-amber-100 text-amber-800";
                                    } else {
                                        licLabel = `${dr} gün (${c.license_until.slice(0, 10)})`;
                                        licClass =
                                            "bg-emerald-100 text-emerald-800";
                                    }
                                }
                                return (
                                    <tr
                                        key={c.id}
                                        className="border-b hover:bg-slate-50"
                                    >
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-2 font-medium">
                                                <Building2 className="w-4 h-4 text-teal-600" />
                                                {c.name}
                                            </div>
                                            <div className="text-[11px] text-slate-500 mt-1">
                                                {c.created_at?.slice(0, 10)}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-slate-700">
                                            {c.owner_email}
                                        </td>
                                        <td className="px-6 py-4">
                                            <span
                                                className={`text-xs font-semibold px-2 py-1 rounded-full ${licClass}`}
                                                data-testid={`license-badge-${c.id}`}
                                            >
                                                {licLabel}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <CreditBadges
                                                data={c}
                                                testPrefix={`credit-badge-${c.id}`}
                                            />
                                        </td>
                                        <td className="px-6 py-4">
                                            <span
                                                className={`text-xs font-semibold px-2 py-1 rounded-full ${c.is_active ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-700"}`}
                                            >
                                                {c.is_active
                                                    ? "Aktif"
                                                    : "Pasif"}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex justify-end gap-2 flex-wrap">
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() =>
                                                        setCreditClinicId(c.id)
                                                    }
                                                    className="text-amber-700 border-amber-200 hover:bg-amber-50"
                                                    data-testid={`credits-clinic-${c.id}`}
                                                >
                                                    <Coins className="w-3.5 h-3.5 mr-1" />
                                                    Kontür
                                                </Button>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() =>
                                                        openLicense(c)
                                                    }
                                                    className="text-teal-700 border-teal-200 hover:bg-teal-50"
                                                    data-testid={`license-clinic-${c.id}`}
                                                >
                                                    <CalendarClock className="w-3.5 h-3.5 mr-1" />
                                                    Lisans
                                                </Button>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() =>
                                                        openEdit(c.id)
                                                    }
                                                    data-testid={`edit-clinic-${c.id}`}
                                                >
                                                    <Pencil className="w-3.5 h-3.5 mr-1" />
                                                    Düzenle
                                                </Button>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() =>
                                                        toggle(c.id)
                                                    }
                                                    data-testid={`toggle-clinic-${c.id}`}
                                                >
                                                    {c.is_active
                                                        ? "Pasifleştir"
                                                        : "Aktifleştir"}
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                            {clinics.length === 0 && (
                                <tr>
                                    <td
                                        colSpan={6}
                                        className="text-center text-slate-500 py-12"
                                    >
                                        Henüz klinik yok. Yukarıdan yeni bir
                                        patron hesabı açın.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </CardContent>
            </Card>

            <CreditDialog
                clinic={creditClinic}
                open={!!creditClinicId}
                onOpenChange={(o) => !o && setCreditClinicId(null)}
                onSaved={load}
            />

            <Dialog open={licOpen} onOpenChange={setLicOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            Lisans Süresi — {licForm.clinic_name}
                        </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={submitLicense} className="space-y-4">
                        <p className="text-sm text-slate-600 leading-relaxed">
                            Kliniğe gün cinsinden lisans süresi tanımlayın. Süre
                            dolduğunda patron ve personelin panele erişimi
                            otomatik olarak kapanır. Mevcut lisans süresine
                            eklenir; süresi geçmişse bugünden itibaren başlar.
                        </p>
                        <div className="grid grid-cols-4 gap-2">
                            {[7, 30, 90, 365].map((n) => (
                                <Button
                                    key={n}
                                    type="button"
                                    variant={
                                        Number(licForm.days) === n
                                            ? "default"
                                            : "outline"
                                    }
                                    size="sm"
                                    onClick={() =>
                                        setLicForm({ ...licForm, days: n })
                                    }
                                    className={
                                        Number(licForm.days) === n
                                            ? "dk-btn-primary"
                                            : ""
                                    }
                                    data-testid={`preset-${n}`}
                                >
                                    {n} gün
                                </Button>
                            ))}
                        </div>
                        <div>
                            <Label>Gün Sayısı</Label>
                            <Input
                                required
                                type="number"
                                min="1"
                                max="3650"
                                value={licForm.days}
                                onChange={(e) =>
                                    setLicForm({
                                        ...licForm,
                                        days: e.target.value,
                                    })
                                }
                                data-testid="license-days-input"
                            />
                        </div>
                        <Button
                            type="submit"
                            className="dk-btn-primary w-full"
                            disabled={licSaving}
                            data-testid="license-submit-btn"
                        >
                            {licSaving ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                `Lisansı ${licForm.days} Gün Uzat`
                            )}
                        </Button>
                    </form>
                </DialogContent>
            </Dialog>

            <Dialog open={editOpen} onOpenChange={setEditOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Patron Bilgilerini Düzenle</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={submitEdit} className="space-y-3">
                        <div>
                            <Label>Klinik Adı</Label>
                            <Input
                                required
                                value={editForm.clinic_name}
                                onChange={(e) =>
                                    setEditForm({
                                        ...editForm,
                                        clinic_name: e.target.value,
                                    })
                                }
                                data-testid="edit-clinic-name-input"
                            />
                        </div>
                        <div>
                            <Label>Patron Adı</Label>
                            <Input
                                required
                                value={editForm.name}
                                onChange={(e) =>
                                    setEditForm({
                                        ...editForm,
                                        name: e.target.value,
                                    })
                                }
                                data-testid="edit-patron-name-input"
                            />
                        </div>
                        <div>
                            <Label>Kullanıcı Adı</Label>
                            <Input
                                required
                                type="text"
                                value={editForm.email}
                                onChange={(e) =>
                                    setEditForm({
                                        ...editForm,
                                        email: e.target.value,
                                    })
                                }
                                data-testid="edit-patron-email-input"
                            />
                        </div>
                        <div>
                            <Label>Yeni Şifre (boş bırakırsanız değişmez)</Label>
                            <Input
                                type="text"
                                placeholder="En az 6 karakter"
                                value={editForm.password}
                                onChange={(e) =>
                                    setEditForm({
                                        ...editForm,
                                        password: e.target.value,
                                    })
                                }
                                data-testid="edit-patron-password-input"
                            />
                        </div>
                        <Button
                            type="submit"
                            disabled={editSaving}
                            className="dk-btn-primary w-full"
                            data-testid="edit-patron-submit-btn"
                        >
                            {editSaving ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                "Değişiklikleri Kaydet"
                            )}
                        </Button>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
